import React, { useCallback, useRef, useState } from 'react';
import { shallowEqual, useSelector, useIntl } from 'umi';

import type { IUseStreamingConversationResult } from '@dbl/core/global';
import type { IBuildAiAnalysisInputParams } from '../runAiAnalysis';
import type {
  IAiAnalysisCardRuntimeApi,
  IAiAnalysisStreamingInput,
  IAiPromptInfo,
  IAiPromptOption,
  IAiScopeTabItem,
} from '../types';

import AiAnalysisModalHost from '../AiAnalysisModalHost';
import { useAiPromptOptions } from './useAiPromptOptions';
import { useAiAnalysisDraftSessionPool } from './useAiAnalysisDraftSessionPool';
import { dashboardPageBtnPoints } from '@/pages/datalab/utils/buiredDatalabBtnPoints/dashboardPageBtnPoint';

/**
 * useAiAnalysisController：AiAnalysis 的"控制器"Hook。
 *
 * 职责边界：
 * - 管理全局唯一的弹窗实例（AiAnalysisModalHost）与其可见性/当前激活 card；
 * - 持有卡片 runtimeApi 的注册表，用于在弹窗 Confirm/Apply 时调度对应卡片；
 * - 维护 previewSession 与 attachedSession 的会话归属（由 draftSessionPool 提供）。
 *
 * 关键交互契约：
 * - Confirm：只更新当前卡片的 promptInfo（编辑态需持久化到布局）；卡片会触发独立会话重跑；
 * - Apply：卡片接管 preview 的同一条流式会话（不重发请求），以实现"所见即所得"的报告延续。
 */
export interface IAiAnalysisControllerQueries {
  filters?: unknown[] | null;
  user_filter?: unknown;
  [key: string]: unknown;
}

export interface IAiAnalysisControllerParams {
  isEdit: boolean;
  gameCode: string;
  dashboardId: string;
  queries: IAiAnalysisControllerQueries;
  scopeTabs: IAiScopeTabItem[];
  enabled: boolean; // 是否存在 AI 卡片（控制 promptOptions 请求）

  onConfirmPromptInfo: (cardId: string, promptInfo: IAiPromptInfo) => void;
}

export interface IAiAnalysisControllerResult {
  modalNode: import('react').ReactNode;
  promptOptions: IAiPromptOption[];
  attachedCardId?: string;
  attachedSessionId: string;
  attachedSession: IUseStreamingConversationResult<IAiAnalysisStreamingInput>;
  runAttachedAnalysisByCardId: (cardId: string, params: IBuildAiAnalysisInputParams) => Promise<void>;

  onOpenAiAnalysisModal: (args: {
    cardId: string;
    entryMode: 'edit' | 'preview';
    defaultSelectedApproachId?: number | string | undefined;
    popupContainer?: HTMLElement | null;
  }) => void;
  onRegisterRuntimeApi: (cardId: string, api: IAiAnalysisCardRuntimeApi) => void;
  stopCardStreamingSession: (cardId: string, fallbackStop: () => void) => void;

  onCloseAiAnalysisModal: () => void;

  handleAiAnalysisCardDeleted: (deletedCardId: string) => void;
  releaseAttachedSessionByCardId: (cardId: string) => void;

  /** 设置 Preview 按钮触发的 AI 分析上报上下文。在发起分析前调用。 */
  setAiMetricsForPreview: (promptId: string) => void;
  /** 设置 Confirm 按钮触发的 AI 分析上报上下文。在发起分析前调用。 */
  setAiMetricsForConfirm: (promptId: string) => void;
}

export const useAiAnalysisController = (params: IAiAnalysisControllerParams): IAiAnalysisControllerResult => {
  const {
    isEdit,
    gameCode,
    dashboardId,
    queries,
    scopeTabs,
    enabled,
    onConfirmPromptInfo,
  } = params;

  const { formatMessage } = useIntl();
  const promptOptions = useAiPromptOptions({ gameCode, dashboardId, enabled });

  // 获取当前游戏的名称，用于埋点上报。
  const currentGame = useSelector((state: any) => state.datalab?.currentGame, shallowEqual);

  // draftSessionPool 维护"弹窗预览会话"和"被卡片接管的会话"。
  // controller 本身不直接创建 SSE，只负责消费这两个会话并把它们分发给卡片/弹窗。
  const {
    attachedCardId,
    attachedSessionId,
    attachedSession,
    previewSession,
    preparePreviewSession,
    runPreviewAnalysis,
    runAttachedAnalysisByCardId,
    detachPreviewToCard,
    discardPreviewSession,
    cleanupOnDeleteCard,
    releaseAttachedSessionByCardId,
    isCardAttached,
    stopAttachedSessionByCardId,
    setAiMetricsCallback: setPoolAiMetricsCallback,
  } = useAiAnalysisDraftSessionPool({
    gameCode,
    dashboardId,
    queries,
    formatMessage,
  });

  // ===== 弹窗状态：当前是否显示、正在哪张卡片上操作、默认高亮哪个方案 =====
  // 这三个 state 共同决定了 AiAnalysisModalHost 的"上下文"。
  // 注意这里是"全局唯一弹窗"的状态，而不是某张卡片局部状态。
  const [aiAnalysisModalVisible, setAiAnalysisModalVisible] = useState(false);
  const [aiAnalysisActiveCardId, setAiAnalysisActiveCardId] = useState<string | undefined>(undefined);
  const [aiAnalysisDefaultSelectedApproachId, setAiAnalysisDefaultSelectedApproachId] = useState<number | string | undefined>(undefined);
  const [aiAnalysisPopupContainer, setAiAnalysisPopupContainer] = useState<HTMLElement | null>(null);

  // cardRuntimeApiMapRef 充当"cardId -> 运行时能力"的注册表。
  // 为什么用 ref 而不是 state：
  // - 这些 API 只供 controller 在事件里调用，不参与渲染；
  // - 如果用 state，注册/更新每张卡片 API 都会触发 controller 重渲染，收益很低。
  const cardRuntimeApiMapRef = useRef<Map<string, IAiAnalysisCardRuntimeApi>>(new Map());

  /**
   * 调度当前激活卡片重新运行分析。
   * 触发场景主要是：
   * - setting 视图直接 Confirm，但并没有有效 preview 会话可接管；
   * - Apply 走 fallback 逻辑时，需要退回到"卡片自己的独立会话重跑"。
   */
  const runActiveCardAnalysis = useCallback(async (promptInfo: IAiPromptInfo) => {
    if (!aiAnalysisActiveCardId) {
      return;
    }
    const activeCardApi = cardRuntimeApiMapRef.current.get(aiAnalysisActiveCardId);
    if (!activeCardApi) {
      return;
    }
    await activeCardApi.runCardAnalysisByPromptInfo(promptInfo);
  }, [aiAnalysisActiveCardId]);

  /**
   * Apply-and-Exit 的核心编排。
   *
   * 分成两条路径：
   * 1. previewSession 里已经有内容或仍在 streaming：
   *    说明用户确实做过预览，此时应该"接管同一条会话"，保持预览内容与卡片内容连续。
   * 2. previewSession 是空的：
   *    常见于 setting 视图直接 Confirm，此时没有可接管的会话，只能让卡片独立重跑。
   */
  const applyPreviewSessionToActiveCard = useCallback(async (promptInfo: IAiPromptInfo) => {
    if (!aiAnalysisActiveCardId) {
      return;
    }
    const activeCardApi = cardRuntimeApiMapRef.current.get(aiAnalysisActiveCardId);
    const canDetachPreview = previewSession.conversations.length > 0 || previewSession.streamingState !== 'idle';
    if (canDetachPreview) {
      // Apply 时如果卡片自身正在跑一条会话，需要先取消，避免"卡片会话与接管的 preview 会话"并发占用并产生错配取消。
      // stop 属于 best-effort：即使取消失败，也不能阻断 Apply 的"会话接管"主流程。
      await activeCardApi?.stopRunningAnalysis().catch(() => {});
      // Apply 接管后不发起新分析，清除 Confirm 设置的埋点回调，防止残留到后续请求误上报。
      activeCardApi?.setAiMetricsCallback(null);
      // Apply 语义：只切换会话归属，卡片接管 preview 的同一条流式会话；不得触发新的分析请求。
      detachPreviewToCard(aiAnalysisActiveCardId);
      return;
    }

    // fallback：未执行过 Preview（例如 setting 视图直接 Confirm），仍沿用"卡片独立会话重跑"语义，避免卡片被接管到空会话。
    await runActiveCardAnalysis(promptInfo);
  }, [
    aiAnalysisActiveCardId,
    detachPreviewToCard,
    previewSession.conversations.length,
    previewSession.streamingState,
    runActiveCardAnalysis,
  ]);

  /**
   * 卡片在挂载时把自己的 runtimeApi 注册进来，供 controller 后续调度。
   * 当前仅做 set，不做 delete：
   * - 本模块里删除卡片时会走 cleanupOnDeleteCard，且 cardId 不会在同一时刻被复用；
   * - 若未来出现 card 频繁复用/卸载重建，可再补 unregister 机制。
   */
  const onRegisterRuntimeApi = useCallback((cardId: string, api: IAiAnalysisCardRuntimeApi) => {
    cardRuntimeApiMapRef.current.set(cardId, api);
  }, []);

  /**
   * 停止卡片当前展示的会话。
   *
   * 为什么需要 fallbackStop：
   * - controller 只知道"这张卡是否接管了 preview 会话"，不知道卡片自己的 stop 细节；
   * - 对未接管的卡片，直接调用卡片传入的 fallback 即可，避免 controller 反向依赖卡片内部实现。
   */
  const stopCardStreamingSession = useCallback((cardId: string, fallbackStop: () => void) => {
    if (isCardAttached(cardId)) {
      stopAttachedSessionByCardId(cardId);
      return;
    }
    Promise.resolve(fallbackStop()).catch(() => {});
  }, [isCardAttached, stopAttachedSessionByCardId]);

  /**
   * 打开弹窗时只记录"操作目标卡片"和"默认选中的方案"，不在这里做预览初始化。
   * 真正的 previewSession 准备发生在 ModalHost 的 visible effect 中，
   * 这样可以把"弹窗显示"和"预览会话生命周期"绑定到同一处。
   *
   * 依赖数组保持为空是有意设计：
   * - 回调里只使用了 React 保证稳定引用的 state setter；
   * - 不捕获外部易变化值，避免函数标识频繁变化影响下游 memo 化。
   */
  const onOpenAiAnalysisModal = useCallback((args: {
    cardId: string;
    entryMode: 'edit' | 'preview';
    defaultSelectedApproachId?: number | string | undefined;
    popupContainer?: HTMLElement | null;
  }) => {
    setAiAnalysisActiveCardId(args.cardId);
    setAiAnalysisDefaultSelectedApproachId(args.defaultSelectedApproachId);
    setAiAnalysisPopupContainer(args.popupContainer ?? null);
    setAiAnalysisModalVisible(true);
  }, []);

  /**
   * Confirm 的职责只有两件事：
   * - 先把当前卡片内存中的 promptInfo 更新掉，保证 UI 和后续再次打开时能回显最新配置；
   * - 编辑态下，再把变更同步回 layout 层持久化。
   *
   * 注意这里不直接负责"是否重新发请求"：
   * - 直接 Confirm 时由卡片自己的 effect/runtimeApi 决定是否重跑；
   * - Apply 时则走上面的 applyPreviewSessionToActiveCard。
   */
  const handleConfirmPromptInfo = useCallback((nextPromptInfo: IAiPromptInfo) => {
    if (!aiAnalysisActiveCardId) {
      return;
    }
    const api = cardRuntimeApiMapRef.current.get(aiAnalysisActiveCardId);
    api?.setCurrentPromptInfo(nextPromptInfo);
    if (isEdit) {
      onConfirmPromptInfo(aiAnalysisActiveCardId, nextPromptInfo);
    }
  }, [aiAnalysisActiveCardId, isEdit, onConfirmPromptInfo]);

  // ===== AI 分析埋点上报 =====
  /**
   * 组装并上传 AI 分析耗时埋点。
   * 由 Preview/Confirm 各自 session 的 __metrics__ 回调触发。
   */
  const uploadAiMetrics = useCallback((params: {
    durationMs: number;
    promptId: string;
    buttonType: 'preview' | 'confirm';
  }) => {
    const { durationMs, promptId, buttonType } = params;
    const promptName = promptOptions.find(
      (o) => String(o.value) === promptId,
    )?.text ?? '';

    dashboardPageBtnPoints({
      extInfo: {
        cardType: 'AI Analysis Card',
        promptName,
        promptId,
        from: isEdit ? '编辑' : '预览',
        time: String(durationMs),
      },
      buttonId: buttonType === 'confirm' ? '101' : '102',
      buttonName: buttonType === 'confirm' ? 'Confirm' : 'Preview',
      uid: gameCode,
      gameName: currentGame?.game_name ?? '',
    });
  }, [currentGame?.game_name, gameCode, isEdit, promptOptions]);

  /**
   * 设置 Preview 分析完成后的埋点回调。
   * 在 AiSettings 点击 Preview 时调用，设置在 previewSession 上。
   */
  const setAiMetricsForPreview = useCallback((promptId: string) => {
    setPoolAiMetricsCallback((durationMs: number) => {
      uploadAiMetrics({ durationMs, promptId, buttonType: 'preview' });
    });
  }, [setPoolAiMetricsCallback, uploadAiMetrics]);

  /**
   * 设置 Confirm 分析完成后的埋点回调。
   * 在 AiSettings 点击 Confirm 时调用，设置在当前激活卡片的独立 session 上。
   */
  const setAiMetricsForConfirm = useCallback((promptId: string) => {
    if (!aiAnalysisActiveCardId) {
      return;
    }
    const activeCardApi = cardRuntimeApiMapRef.current.get(aiAnalysisActiveCardId);
    if (!activeCardApi) {
      return;
    }
    activeCardApi.setAiMetricsCallback((durationMs: number) => {
      uploadAiMetrics({ durationMs, promptId, buttonType: 'confirm' });
    });
  }, [aiAnalysisActiveCardId, uploadAiMetrics]);

  // 关闭请求只负责收起弹窗；preview 会话是否要清理由 ModalHost 根据 didApply 决定。
  const handleRequestClose = useCallback(() => {
    setAiAnalysisModalVisible(false);
    setAiAnalysisPopupContainer(null);
  }, []);

  /**
   * 卡片被删除时需要同步清理两类状态：
   * - previewSession：如果弹窗还开着或刚做过预览，先丢弃掉，避免残留流式任务；
   * - attachedSession：如果删掉的是当前接管会话的卡片，要把会话一起终止。
   */
  const handleAiAnalysisCardDeleted = useCallback((deletedCardId: string) => {
    discardPreviewSession();
    cleanupOnDeleteCard(deletedCardId);
    if (aiAnalysisActiveCardId === deletedCardId) {
      setAiAnalysisModalVisible(false);
      setAiAnalysisPopupContainer(null);
    }
  }, [aiAnalysisActiveCardId, cleanupOnDeleteCard, discardPreviewSession]);

  // IMPORTANT:
  // 不要用 useMemo 固定依赖 `aiAnalysisActiveCardId`，否则 Confirm 后 currentPromptInfo 已更新但 initialPromptInfo 不会刷新，
  // 会导致 Settings 再次打开时无法回显自定义 prompt 内容。
  const initialPromptInfo = aiAnalysisActiveCardId
    ? cardRuntimeApiMapRef.current.get(aiAnalysisActiveCardId)?.getCurrentPromptInfo()
    : undefined;

  // modalNode 直接在 Hook 内组装，方便外层页面把"控制器能力"和"弹窗节点"一起消费。
  const modalNode = (
    <AiAnalysisModalHost
      visible={aiAnalysisModalVisible}
      activeCardId={aiAnalysisActiveCardId}
      isEdit={isEdit}
      gameCode={gameCode}
      dashboardId={dashboardId}
      scopeTabs={scopeTabs}
      promptOptions={promptOptions}
      defaultSelectedApproachId={aiAnalysisDefaultSelectedApproachId}
      popupContainer={aiAnalysisPopupContainer}
      initialPromptInfo={initialPromptInfo}
      onConfirmPromptInfo={handleConfirmPromptInfo}
      onRequestClose={handleRequestClose}
      previewSession={previewSession}
      preparePreviewSession={preparePreviewSession}
      runPreviewAnalysis={runPreviewAnalysis}
      onApplyToActiveCard={applyPreviewSessionToActiveCard}
      discardPreviewSession={discardPreviewSession}
      setAiMetricsForPreview={setAiMetricsForPreview}
      setAiMetricsForConfirm={setAiMetricsForConfirm}
    />
  );

  return {
    modalNode,
    promptOptions,
    attachedCardId,
    attachedSessionId,
    attachedSession,
    runAttachedAnalysisByCardId,
    onOpenAiAnalysisModal,
    onRegisterRuntimeApi,
    stopCardStreamingSession,
    onCloseAiAnalysisModal: handleRequestClose,
    handleAiAnalysisCardDeleted,
    releaseAttachedSessionByCardId,
    setAiMetricsForPreview,
    setAiMetricsForConfirm,
  };
};

